// ./core/UI/overlayelements/ConnectionStatusOverlay.tsx
import React, { useEffect, useState } from 'react';
import { useActiveGameStore } from '@core/gameengine/state/activeGameStore';
import { useT } from '@core/i18n';
import { getGameFromUrl } from '@core/utils/urlParser';
import { isSkyxNewRoute } from '@core/utils/gameRouteSlugs';
import { CONNECTION_CONFIG } from '@Web/config/ConnectionConfig';
import { GlobalBus, UI_DISMISS_POPUPS } from '@core/Bus/EventBus';
import {
  SKYX_NEW_CLOSE_CHAT,
  SKYX_NEW_CLOSE_MENU,
} from '@core/lobby/skyxNewPanelEvents';
import { styles, keyframes } from '../styles/ConnectionStatusOverlay.styles';
import { resolveConnectionOverlayView } from './connectionOverlayView';

/** Kept for reuse; not currently called from this overlay. */
function _dismissOpenPopups(): void {
  GlobalBus.emit(UI_DISMISS_POPUPS);
  GlobalBus.emit(SKYX_NEW_CLOSE_MENU);
  GlobalBus.emit(SKYX_NEW_CLOSE_CHAT);
}

export const ConnectionStatusOverlay: React.FC = () => {
  const t = useT();
  const connectionStatus = useActiveGameStore(
    s => s.connectionStatus as string
  );
  const isSkyxNew = isSkyxNewRoute(getGameFromUrl());
  const isInterrupted =
    connectionStatus === 'reconnecting' || connectionStatus === 'disconnected';
  const [reloadRequired, setReloadRequired] = useState(false);

  useEffect(() => {
    if (connectionStatus === 'connected') {
      setReloadRequired(false);
      return;
    }
    if (connectionStatus === 'failed') {
      setReloadRequired(true);
    }
  }, [connectionStatus]);

  useEffect(() => {
    if (reloadRequired || !isInterrupted) {
      return;
    }
    const timer = window.setTimeout(() => {
      setReloadRequired(true);
    }, CONNECTION_CONFIG.ui.longFailureThresholdMs);
    return () => window.clearTimeout(timer);
  }, [isInterrupted, reloadRequired]);

  const overlayView = resolveConnectionOverlayView({
    connectionStatus,
    isSkyxNew,
    reloadRequired,
  });

  if (overlayView === 'reload') {
    return (
      <div style={styles.overlayStyle}>
        <style>{keyframes}</style>
        <div style={styles.spinnerStyle}></div>
        <h2 style={styles.textStyle}>{t('ui.system.connection_failed')}</h2>
        <p style={styles.subTextStyle}>
          {t('ui.system.connection_failed_detail')}
        </p>
        <button
          style={styles.buttonStyle}
          onClick={() => window.location.reload()}
          onMouseOver={e => (e.currentTarget.style.backgroundColor = '#218838')}
          onMouseOut={e => (e.currentTarget.style.backgroundColor = '#28A745')}
        >
          {t('ui.system.refresh_page')}
        </button>
      </div>
    );
  }

  if (overlayView === 'hidden') {
    return null;
  }

  if (overlayView === 'connecting') {
    return (
      <div style={styles.overlayStyle}>
        <style>{keyframes}</style>
        <div style={styles.spinnerStyle}></div>
        <h2 style={styles.textStyle}>{t('ui.system.connecting')}</h2>
      </div>
    );
  }

  if (overlayView === 'interrupted') {
    return (
      <div style={styles.overlayStyle}>
        <style>{keyframes}</style>
        <div style={styles.spinnerStyle}></div>
        <h2 style={styles.textStyle}>{t('ui.system.reconnecting')}</h2>
      </div>
    );
  }

  return null;
};
